/**
 * デザイントークン定義
 *
 * このファイルは既存コードに影響を与えず、
 * 参照可能な定数として追加されています。
 *
 * 使用方法:
 * import { designTokens } from './theme/designTokens';
 * color: designTokens.colors.primary
 */

export const designTokens = {
  // カラーパレット
  colors: {
    // プライマリカラー（独自ブランドカラー）
    primary: '#249BE0',  // ティールブルー - 信頼感と専門性を表現
    secondary: '#0D7C8C', // ディープティール
    accent: '#32A287',   // アクセントグリーン

    // セマンティックカラー
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    // ニュートラルカラー（ブルーティンテッド - ブランドカラーとの調和）
    neutral: {
      50: '#F7F8FA',
      100: '#F2F4F7',
      200: '#E8ECF0',
      300: '#DDE2E8',
      400: '#B0B8C4',
      500: '#8B95A3',
      600: '#6B7585',
      700: '#525D6E',
      800: '#374151',
      900: '#1F2937',
    },

    // 白と黒
    white: '#ffffff',
    black: '#000000',
  },

  // シャドウ（控えめな値）
  shadows: {
    none: 'none',
    sm: '0 1px 3px rgba(0,0,0,0.12)',
    md: '0 4px 6px rgba(0,0,0,0.1)',
    lg: '0 10px 15px rgba(0,0,0,0.1)',
    xl: '0 20px 25px rgba(0,0,0,0.15)',
  },

  // ボーダー半径
  borderRadius: {
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
  },

  // トランジション
  transitions: {
    fast: '0.15s ease',
    normal: '0.3s ease',
    slow: '0.5s ease',
  },

  // スペーシング（Material-UIの8pxグリッドに準拠）
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },

  // 状態色（進捗、難易度等で使用）
  statusColors: {
    high: {
      primary: '#4caf50',
      light: '#8bc34a',
    },
    medium: {
      primary: '#f57c00',
      light: '#ff9800',
    },
    low: {
      primary: '#f44336',
      light: '#ff5722',
    },
  },

  // 難易度カラー
  difficultyColors: {
    veryEasy: '#4caf50',
    easy: '#8bc34a',
    slightlyDifficult: '#ffb74d',
    difficult: '#f44336',
    veryDifficult: '#d32f2f',
  },

  // 予算配分カラー
  budgetColors: {
    media: '#ff7043',
    agency: '#ab47bc',
    others: '#42a5f5',
  },

  // 機能別テーマカラー（アクセントボーダー用）
  featureThemes: {
    recruit_plan: {
      primary: '#f37335',      // 採用計画アシスト（オレンジ）
      light: '#ff9966',
      shadow: 'rgba(243, 115, 53, 0.12)',
      shadowHover: 'rgba(243, 115, 53, 0.2)',
    },
    media_selection: {
      primary: '#2196F3',      // 募集媒体選定（青）
      light: '#64B5F6',
      shadow: 'rgba(33, 150, 243, 0.12)',
      shadowHover: 'rgba(33, 150, 243, 0.2)',
    },
    job_posting: {
      primary: '#4CAF50',      // 求人票作成（緑）
      light: '#81C784',
      shadow: 'rgba(76, 175, 80, 0.12)',
      shadowHover: 'rgba(76, 175, 80, 0.2)',
    },
    interview_support: {
      primary: '#9C27B0',      // 面接サポート（紫）
      light: '#BA68C8',
      shadow: 'rgba(156, 39, 176, 0.12)',
      shadowHover: 'rgba(156, 39, 176, 0.2)',
    },
  },

  // タイポグラフィ階層
  typography: {
    // 見出し
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },

    // 本文
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },

    // 数値・データ強調表示
    dataDisplay: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1,
      fontVariantNumeric: 'tabular-nums', // 数字の幅を統一
    },
    dataDisplayMedium: {
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.1,
      fontVariantNumeric: 'tabular-nums',
    },
    dataDisplaySmall: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.2,
      fontVariantNumeric: 'tabular-nums',
    },

    // キャプション
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
    },
  },
};

// 共通カードスタイル（全カードで使用）
// muiTheme引数でダークモード対応（省略時はライトモードデフォルト）
export const commonCardStyles = {
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: `1px solid ${designTokens.colors.neutral[200]}`,
  boxShadow: designTokens.shadows.sm,
  overflow: 'hidden',
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: designTokens.shadows.md,
    transform: 'translateY(-2px)', // 軽い浮遊効果
  },
};

/** ダークモード対応版の共通カードスタイル */
export const commonCardStylesDark = (muiTheme) => {
  const isDark = muiTheme?.palette?.mode === 'dark';
  return {
    backgroundColor: isDark ? (muiTheme?.palette?.background?.paper || '#1E1E1E') : '#ffffff',
    borderRadius: '12px',
    border: isDark ? '1px solid rgba(255,255,255,0.08)' : `1px solid ${designTokens.colors.neutral[200]}`,
    boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.4)' : designTokens.shadows.sm,
    overflow: 'hidden',
    position: 'relative',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: isDark ? '0 4px 6px rgba(0,0,0,0.4)' : designTokens.shadows.md,
      transform: 'translateY(-2px)',
    },
  };
};

/**
 * アクセントボーダーカードスタイル
 * 機能別にテーマカラーのボーダーを付与し、視覚的に区別可能にする
 *
 * @param {string} featureKey - 機能キー ('recruit_plan' | 'media_selection' | 'job_posting')
 * @returns {Object} カードスタイルオブジェクト
 *
 * 使用例:
 * import { accentBorderCard } from './theme/designTokens';
 * sx={accentBorderCard('recruit_plan')}
 */
export const accentBorderCard = (featureKey, muiTheme) => {
  const theme = designTokens.featureThemes[featureKey];
  const isDark = muiTheme?.palette?.mode === 'dark';
  const bgColor = isDark ? (muiTheme?.palette?.background?.paper || '#1E1E1E') : '#ffffff';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : designTokens.colors.neutral[200];

  if (!theme) {
    console.warn(`Unknown feature theme: ${featureKey}. Falling back to default.`);
    return commonCardStylesDark(muiTheme);
  }

  return {
    backgroundColor: bgColor,
    borderRadius: '12px',
    border: `1px solid ${borderColor}`,
    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : `0 2px 8px ${theme.shadow}`,
    overflow: 'hidden',
    position: 'relative',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : `0 4px 12px ${theme.shadowHover}`,
      transform: 'translateY(-2px)',
    },
  };
};

/**
 * セクションヘッダースタイル（機能別テーマカラー）
 * カードのセクション見出しにテーマカラーの背景を適用
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} セクションヘッダースタイル
 */
export const sectionHeader = (featureKey) => {
  const theme = designTokens.featureThemes[featureKey];

  if (!theme) {
    return {
      padding: '12px 16px',
      backgroundColor: designTokens.colors.neutral[100],
      borderRadius: '12px 12px 0 0',
      marginBottom: 2,
    };
  }

  return {
    padding: '12px 16px',
    backgroundColor: `${theme.primary}08`, // 3%透明度
    borderRadius: '8px',
    marginBottom: 2,
    '& .MuiTypography-root': {
      color: theme.primary,
      fontWeight: 600,
    },
  };
};

/**
 * セグメントコントロール型タブスタイル（iOS/iPadOS風）
 * 機能別テーマカラーを使用した現代的なタブデザイン
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} タブコンテナスタイル
 */
export const segmentedTabsContainer = (featureKey) => {
  const theme = designTokens.featureThemes[featureKey];

  if (!theme) {
    return {
      maxWidth: '600px',
      margin: '0 auto',
      marginBottom: '24px',
      borderRadius: '12px',
      backgroundColor: designTokens.colors.neutral[100],
      padding: '6px',
      minHeight: '48px',
    };
  }

  return {
    maxWidth: '600px',
    margin: '0 auto',
    marginBottom: '24px',
    borderRadius: '12px',
    backgroundColor: `${theme.primary}08`, // 3%透明度
    padding: '6px',
    minHeight: '48px',
    '& .MuiTabs-flexContainer': {
      gap: '4px',
    },
    '& .MuiTabs-indicator': {
      display: 'none', // アンダーラインを非表示
    },
  };
};

/**
 * セグメントコントロール型タブボタンスタイル
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} タブボタンスタイル
 */
export const segmentedTab = (featureKey) => {
  const theme = designTokens.featureThemes[featureKey];

  if (!theme) {
    return {
      textTransform: 'none',
      minWidth: 0,
      flex: 1,
      fontWeight: 600,
      fontSize: '0.95rem',
      borderRadius: '8px',
      padding: '10px 16px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      color: designTokens.colors.neutral[600],
    };
  }

  return {
    textTransform: 'none',
    minWidth: 0,
    flex: 1,
    fontWeight: 600,
    fontSize: '0.95rem',
    borderRadius: '8px',
    padding: '10px 16px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    color: theme.primary,
    '&.Mui-selected': {
      color: '#ffffff',
      backgroundColor: theme.primary,
      boxShadow: `0 2px 8px ${theme.shadow}`,
    },
    '&:hover:not(.Mui-selected)': {
      backgroundColor: `${theme.primary}15`,
    },
  };
};

/**
 * ミニマル・アイコンボタンスタイル（2025年トレンド）
 * アイコンのみの円形ボタン、機能別テーマカラー対応
 *
 * @param {string} featureKey - 機能キー（省略時はニュートラル）
 * @returns {Object} アイコンボタンスタイル
 *
 * 使用例:
 * <IconButton sx={minimalIconButton('recruit_plan')}>
 *   <ForumIcon />
 * </IconButton>
 */
export const minimalIconButton = (featureKey = null) => {
  const theme = featureKey ? designTokens.featureThemes[featureKey] : null;
  const baseColor = theme?.primary || designTokens.colors.neutral[600];
  const hoverBg = theme?.primary ? `${theme.primary}15` : designTokens.colors.neutral[100];

  return {
    width: 48,
    height: 48,
    borderRadius: '50%',
    color: baseColor,
    backgroundColor: 'transparent',
    border: `2px solid ${designTokens.colors.neutral[200]}`,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: designTokens.shadows.sm,
    '&:hover': {
      backgroundColor: hoverBg,
      borderColor: baseColor,
      boxShadow: `0 4px 12px ${theme?.shadow || 'rgba(0,0,0,0.1)'}`,
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: designTokens.shadows.sm,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.5rem',
    },
  };
};

/**
 * チャットアイコンボタンスタイル（バッジ付き）
 * チャットモードの状態を視覚的に表示
 *
 * @param {string} featureKey - 機能キー
 * @param {boolean} isActive - チャットがアクティブかどうか
 * @returns {Object} チャットボタンスタイル
 *
 * 使用例:
 * <IconButton sx={chatIconButton('recruit_plan', chatMode !== 'off')}>
 *   <ForumIcon />
 * </IconButton>
 */
export const chatIconButton = (featureKey, isActive = false) => {
  const theme = designTokens.featureThemes[featureKey];
  const baseStyle = minimalIconButton(featureKey);

  if (!isActive) {
    return baseStyle;
  }

  // アクティブ時はテーマカラー背景
  return {
    ...baseStyle,
    backgroundColor: theme?.primary || designTokens.colors.primary,
    color: '#ffffff',
    border: 'none',
    boxShadow: `0 4px 12px ${theme?.shadow || 'rgba(0,0,0,0.15)'}`,
    '&:hover': {
      backgroundColor: theme?.light || designTokens.colors.primary,
      color: '#ffffff',
      borderColor: 'transparent',
      boxShadow: `0 6px 16px ${theme?.shadowHover || 'rgba(0,0,0,0.2)'}`,
      transform: 'translateY(-2px)',
    },
  };
};

/**
 * メニューアイコンボタンスタイル（縦三点）
 * ミニマルデザインのメニュートリガー
 *
 * @param {string} featureKey - 機能キー（省略可）
 * @returns {Object} メニューボタンスタイル
 */
export const menuIconButton = (featureKey = null) => {
  return {
    ...minimalIconButton(featureKey),
    // メニューボタンは少し小さめでも良い
  };
};

/**
 * チャットヘッダースタイル（2025年トレンド）
 * 機能別テーマカラーを使用した洗練されたヘッダー
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} チャットヘッダースタイル
 */
export const chatHeader = (featureKey, muiTheme) => {
  const theme = designTokens.featureThemes[featureKey];
  const isDark = muiTheme?.palette?.mode === 'dark';
  const bgColor = isDark ? (muiTheme?.palette?.background?.paper || '#1E1E1E') : '#ffffff';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : '#e0e0e0';

  if (!theme) {
    return {
      padding: '16px',
      backgroundColor: bgColor,
      borderBottom: `1px solid ${borderColor}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    };
  }

  return {
    padding: '16px',
    backgroundColor: bgColor,
    borderBottom: `2px solid ${theme.primary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  };
};

/**
 * チャット背景スタイル
 * 機能別テーマカラーの極薄背景
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} 背景スタイル
 */
export const chatBackground = (featureKey, muiTheme) => {
  const theme = designTokens.featureThemes[featureKey];
  const isDark = muiTheme?.palette?.mode === 'dark';
  const fallback = isDark ? '#1E1E1E' : '#ffffff';

  return {
    backgroundColor: theme ? `${theme.primary}${isDark ? '08' : '03'}` : fallback,
  };
};

/**
 * チャット吹き出しスタイル（Bot用）
 * ニュートラルで読みやすいデザイン
 *
 * @returns {Object} Bot吹き出しスタイル
 */
export const chatBubbleBot = (muiTheme) => {
  const isDark = muiTheme?.palette?.mode === 'dark';
  return {
    maxWidth: '70%',
    padding: '12px 16px',
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#F8F9FA',
    color: isDark ? '#E8E8E8' : '#212529',
    borderRadius: '16px 16px 16px 4px',
    boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.3)' : '0 1px 2px rgba(0,0,0,0.08)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };
};

/**
 * チャット吹き出しスタイル（User用）
 * 機能別テーマカラーを使用
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} User吹き出しスタイル
 */
export const chatBubbleUser = (featureKey) => {
  const theme = designTokens.featureThemes[featureKey];

  if (!theme) {
    return {
      maxWidth: '70%',
      padding: '12px 16px',
      backgroundColor: designTokens.colors.primary,
      color: '#ffffff',
      borderRadius: '16px 16px 4px 16px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    };
  }

  return {
    maxWidth: '70%',
    padding: '12px 16px',
    backgroundColor: theme.primary,
    color: '#ffffff',
    borderRadius: '16px 16px 4px 16px',
    boxShadow: `0 2px 8px ${theme.shadow}`,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };
};

/**
 * チャット入力フィールドスタイル
 * 機能別テーマカラーのフォーカス状態
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} 入力フィールドスタイル
 */
export const chatInputField = (featureKey, muiTheme) => {
  const theme = designTokens.featureThemes[featureKey];
  const isDark = muiTheme?.palette?.mode === 'dark';

  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
      borderRadius: '12px',
      '& fieldset': {
        borderColor: isDark ? 'rgba(255,255,255,0.15)' : designTokens.colors.neutral[300],
      },
      '&:hover fieldset': {
        borderColor: theme?.primary || designTokens.colors.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme?.primary || designTokens.colors.primary,
        borderWidth: '2px',
      },
    },
  };
};

/**
 * チャット送信ボタンスタイル
 * 機能別テーマカラーを使用
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} 送信ボタンスタイル
 */
export const chatSendButton = (featureKey) => {
  const theme = designTokens.featureThemes[featureKey];

  if (!theme) {
    return {
      borderRadius: '12px',
      textTransform: 'none',
      fontWeight: 600,
      paddingX: 3,
      paddingY: 1,
      boxShadow: designTokens.shadows.sm,
    };
  }

  return {
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    paddingX: 3,
    paddingY: 1,
    backgroundColor: theme.primary,
    color: '#ffffff',
    boxShadow: `0 2px 8px ${theme.shadow}`,
    '&:hover': {
      backgroundColor: theme.light,
      boxShadow: `0 4px 12px ${theme.shadowHover}`,
    },
    '&:disabled': {
      backgroundColor: designTokens.colors.neutral[300],
      color: designTokens.colors.neutral[500],
    },
  };
};

/**
 * 大型選択カードスタイル（スタート画面用）
 * 機能別テーマカラーを使用した選択可能なカード
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} 選択カードスタイル
 */
export const selectionCard = (featureKey, muiTheme) => {
  const theme = designTokens.featureThemes[featureKey];
  const isDark = muiTheme?.palette?.mode === 'dark';
  const bgColor = isDark ? (muiTheme?.palette?.background?.paper || '#1E1E1E') : '#ffffff';
  const borderColor = isDark ? 'rgba(255,255,255,0.12)' : designTokens.colors.neutral[200];

  if (!theme) {
    return {
      backgroundColor: bgColor,
      borderRadius: '16px',
      border: `2px solid ${borderColor}`,
      padding: '32px',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: isDark ? '0 12px 24px rgba(0,0,0,0.5)' : '0 12px 24px rgba(0,0,0,0.15)',
        borderColor: designTokens.colors.primary,
      },
    };
  }

  return {
    backgroundColor: bgColor,
    borderRadius: '16px',
    border: `2px solid ${borderColor}`,
    padding: '32px',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : `0 2px 8px ${theme.shadow}`,
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: isDark ? '0 12px 24px rgba(0,0,0,0.5)' : `0 12px 24px ${theme.shadowHover}`,
      borderColor: theme.primary,
    },
  };
};

/**
 * 選択カード内のアクションボタンスタイル
 * 機能別テーマカラーを使用
 *
 * @param {string} featureKey - 機能キー
 * @returns {Object} アクションボタンスタイル
 */
export const selectionCardButton = (featureKey) => {
  const theme = designTokens.featureThemes[featureKey];

  if (!theme) {
    return {
      width: '100%',
      borderRadius: '12px',
      textTransform: 'none',
      fontWeight: 600,
      padding: '12px 24px',
      fontSize: '1rem',
    };
  }

  return {
    width: '100%',
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 24px',
    fontSize: '1rem',
    backgroundColor: theme.primary,
    color: '#ffffff',
    boxShadow: `0 2px 8px ${theme.shadow}`,
    '&:hover': {
      backgroundColor: theme.light,
      boxShadow: `0 4px 12px ${theme.shadowHover}`,
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  };
};

// リップル効果付きボタンスタイル
export const buttonWithRipple = {
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 0,
    height: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.6s ease, height 0.6s ease, opacity 0.6s ease',
    opacity: 0,
  },
  '&:active::after': {
    width: '300px',
    height: '300px',
    opacity: 1,
    transition: 'width 0s, height 0s, opacity 0s',
  },
};

// ============================================
// 2026年UIトレンド対応スタイル
// ============================================

/**
 * フォーカスリングスタイル（アクセシビリティ強化）
 * WCAG 2.1準拠のフォーカス表示
 */
export const focusRing = {
  '&:focus-visible': {
    outline: `3px solid rgba(13, 115, 119, 0.5)`,
    outlineOffset: '2px',
  },
};

/**
 * シンプルなカードスタイル
 * 過剰な装飾を排除したクリーンなデザイン
 */
export const liquidGlassCard = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  borderRadius: '8px',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
};

/** ダークモード対応版 liquidGlassCard */
export const liquidGlassCardDark = (muiTheme) => {
  const isDark = muiTheme?.palette?.mode === 'dark';
  return {
    background: isDark ? (muiTheme?.palette?.background?.paper || '#1E1E1E') : '#ffffff',
    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
    boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
    borderRadius: '8px',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
  };
};

/**
 * モーダルスタイル
 */
export const liquidGlassModal = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
  borderRadius: '12px',
};

/** ダークモード対応版 liquidGlassModal */
export const liquidGlassModalDark = (muiTheme) => {
  const isDark = muiTheme?.palette?.mode === 'dark';
  return {
    background: isDark ? (muiTheme?.palette?.background?.paper || '#1E1E1E') : '#ffffff',
    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
    boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.5)' : '0 4px 24px rgba(0, 0, 0, 0.12)',
    borderRadius: '12px',
  };
};

/**
 * オーバーレイ（背景用）
 */
export const liquidGlassOverlay = {
  background: 'rgba(0, 0, 0, 0.4)',
};

/**
 * Bento Grid レイアウトスタイル
 * 日本の弁当箱に着想を得たモジュラーレイアウト
 */
export const bentoGridContainer = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridAutoRows: 'minmax(120px, auto)',
  gap: '16px',
  '@media (max-width: 1200px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
};

/**
 * Bento Grid アイテムサイズバリエーション
 */
export const bentoGridItem = {
  // 1x1 (標準)
  standard: {
    gridColumn: 'span 1',
    gridRow: 'span 1',
  },
  // 2x1 (横長)
  wide: {
    gridColumn: 'span 2',
    gridRow: 'span 1',
  },
  // 1x2 (縦長)
  tall: {
    gridColumn: 'span 1',
    gridRow: 'span 2',
  },
  // 2x2 (大)
  large: {
    gridColumn: 'span 2',
    gridRow: 'span 2',
  },
  // フル幅
  full: {
    gridColumn: '1 / -1',
    gridRow: 'span 1',
  },
};

/**
 * Bento カードスタイル
 */
export const bentoCard = {
  ...liquidGlassCard,
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: '100%',
};

/**
 * KPI カードスタイル（プライマリ - 大きい表示）
 */
export const kpiCardPrimary = {
  ...bentoCard,
  background: '#ffffff',
  '& .kpi-value': {
    fontSize: '3rem',
    fontWeight: 600,
    lineHeight: 1,
    color: '#249BE0',
  },
  '& .kpi-label': {
    fontSize: '1rem',
    fontWeight: 500,
    color: designTokens.colors.neutral[600],
  },
};

/** ダークモード対応版 kpiCardPrimary */
export const kpiCardPrimaryDark = (muiTheme) => {
  const isDark = muiTheme?.palette?.mode === 'dark';
  return {
    ...liquidGlassCardDark(muiTheme),
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '100%',
    '& .kpi-value': {
      fontSize: '3rem',
      fontWeight: 600,
      lineHeight: 1,
      color: isDark ? '#5BB8F0' : '#249BE0',
    },
    '& .kpi-label': {
      fontSize: '1rem',
      fontWeight: 500,
      color: isDark ? '#B0B0B0' : designTokens.colors.neutral[600],
    },
  };
};

/**
 * KPI カードスタイル（セカンダリ - 小さい表示）
 */
export const kpiCardSecondary = {
  ...bentoCard,
  padding: '16px',
  '& .kpi-value': {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  '& .kpi-label': {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: designTokens.colors.neutral[500],
  },
};

/**
 * 空状態（Empty State）スタイル
 */
export const emptyStateStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '48px 24px',
    minHeight: '300px',
  },
  icon: {
    fontSize: '64px',
    color: designTokens.colors.neutral[300],
    marginBottom: '24px',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: designTokens.colors.neutral[700],
    marginBottom: '8px',
  },
  description: {
    fontSize: '0.95rem',
    color: designTokens.colors.neutral[500],
    marginBottom: '24px',
    maxWidth: '400px',
  },
};

/**
 * 機能別 Empty State スタイル
 * @param {string} featureKey - 機能キー
 */
export const emptyStateWithTheme = (featureKey) => {
  const theme = designTokens.featureThemes[featureKey];

  return {
    ...emptyStateStyles,
    icon: {
      ...emptyStateStyles.icon,
      color: theme?.light || designTokens.colors.neutral[300],
    },
    button: {
      backgroundColor: theme?.primary || designTokens.colors.primary,
      color: '#ffffff',
      '&:hover': {
        backgroundColor: theme?.light || designTokens.colors.primary,
      },
    },
  };
};

/**
 * スケルトンローディングスタイル
 */
export const skeletonStyles = {
  card: {
    borderRadius: '12px',
    backgroundColor: designTokens.colors.neutral[100],
  },
  text: {
    borderRadius: '4px',
    backgroundColor: designTokens.colors.neutral[100],
  },
  circular: {
    backgroundColor: designTokens.colors.neutral[100],
  },
  // アニメーションパルス
  pulse: {
    animation: 'pulse 1.5s ease-in-out infinite',
    '@keyframes pulse': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.4 },
      '100%': { opacity: 1 },
    },
  },
  // アニメーション波
  wave: {
    background: `linear-gradient(
      90deg,
      ${designTokens.colors.neutral[100]} 25%,
      ${designTokens.colors.neutral[200]} 50%,
      ${designTokens.colors.neutral[100]} 75%
    )`,
    backgroundSize: '200% 100%',
    animation: 'wave 1.5s ease-in-out infinite',
    '@keyframes wave': {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
  },
};

/**
 * カラーコントラスト改善（WCAG 4.5:1準拠）
 */
export const accessibleColors = {
  // プレースホルダー（改善後）
  placeholder: '#5c6370', // 以前: #64748b
  // 無効状態
  disabled: {
    text: '#6b7280',      // 以前: #94a3b8
    background: '#e5e7eb',
  },
  // セカンダリテキスト
  textSecondary: '#4b5563', // 以前: alpha 0.5
};

// ============================================
// ローディング画面統一スタイル
// ============================================

/**
 * ローディング画面用スタイル（2026年トレンド対応）
 * ミニマリスト、目的を持ったモーション、高アクセシビリティ
 */
export const loadingScreenStyles = {
  // アイコンコンテナ（控えめなサイズとシャドウ）
  iconContainer: (color) => ({
    width: 120,
    height: 120,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 0 20px 5px ${color}22`,
    background: `${color}11`,
  }),
  // プログレスバー（統一デザイン）
  progressBar: (color) => ({
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(0,0,0,0.08)',
    '& .MuiLinearProgress-bar': {
      backgroundColor: color,
      transition: 'transform 0.3s ease',
    },
  }),
  // オーバーレイカード（再生成時など）— ダーク対応はLoadingScreenコンポーネント側で実施
  overlayCard: {
    textAlign: 'center',
    p: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderRadius: 3,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    width: '100%',
    maxWidth: 400,
  },
  // オーバーレイカード ダークモード対応版
  overlayCardDark: (muiTheme) => {
    const isDark = muiTheme?.palette?.mode === 'dark';
    return {
      textAlign: 'center',
      p: 4,
      backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderRadius: 3,
      boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.08)',
      border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
      width: '100%',
      maxWidth: 400,
    };
  },
  // オーバーレイ背景
  overlayBackdrop: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },
  // オーバーレイ背景 ダークモード対応版
  overlayBackdropDark: (muiTheme) => {
    const isDark = muiTheme?.palette?.mode === 'dark';
    return {
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
    };
  },
  // アニメーション設定（控えめ）
  animation: {
    scale: [1, 1.04, 1], // 1.08から縮小
    duration: 2,
    transition: 0.2, // 0.3から短縮
  },
  // メッセージスタイル
  message: {
    fontWeight: 600,
    color: designTokens.colors.neutral[800],
    textAlign: 'center',
  },
  // ステップカウンタースタイル
  stepCounter: {
    textAlign: 'center',
    fontSize: '13px',
    color: designTokens.colors.neutral[500],
  },
  // 超過メッセージスタイル
  overtimeMessage: {
    textAlign: 'center',
    display: 'block',
    mt: 1,
    fontSize: '12px',
    color: designTokens.colors.warning,
  },
};

/**
 * 機能別ローディングカラーの取得
 * @param {string} featureKey - 機能キー
 * @returns {string} カラーコード
 */
export const getLoadingColor = (featureKey) => {
  const theme = designTokens.featureThemes[featureKey];
  return theme?.primary || '#249BE0';
};

// Material-UIテーマで使用する拡張定義
export const muiThemeExtension = {
  shadows: [
    'none',
    designTokens.shadows.sm,
    designTokens.shadows.sm,
    designTokens.shadows.md,
    designTokens.shadows.md,
    designTokens.shadows.md,
    designTokens.shadows.lg,
    designTokens.shadows.lg,
    designTokens.shadows.lg,
    designTokens.shadows.lg,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
  ],
};
